import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import api from "@/utils/api";

export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref(null);
  const token = ref(localStorage.getItem("token") || null);
  const isLoading = ref(false);
  const router = useRouter();

  // Getters
  const isAuthenticated = computed(() => !!token.value);
  const userRole = computed(() => user.value?.role || null);
  const userName = computed(() => user.value?.name || "");
  const userAvatar = computed(() => user.value?.avatar || "");

  // Actions
  const login = async (credentials) => {
    isLoading.value = true;

    try {
      const response = await api.post("/api/v1/auth/login", {
        email: credentials.email,
        password: credentials.password,
      });
      console.error("Login success:", response);
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      return true;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const logout = async () => {
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Clear state
      user.value = null;
      token.value = null;

      // Clear storage
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");

      // Redirect to login
      router.push("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const checkAuth = () => {
    // Check for existing session
    const storedToken =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    const storedUser =
      localStorage.getItem("user") || sessionStorage.getItem("user");

    if (storedToken && storedUser) {
      token.value = storedToken;
      user.value = JSON.parse(storedUser);
      return true;
    }

    return false;
  };

  const refreshToken = async () => {
    // In production, this would refresh the JWT token
    try {
      // Simulate token refresh
      await new Promise((resolve) => setTimeout(resolve, 500));
      return true;
    } catch (error) {
      console.error("Token refresh error:", error);
      return false;
    }
  };

  // Initialize auth state
  const init = () => {
    checkAuth();
  };

  return {
    // State
    user,
    token,
    isLoading,

    // Getters
    isAuthenticated,
    userRole,
    userName,
    userAvatar,

    // Actions
    login,
    logout,
    checkAuth,
    refreshToken,
    init,
  };
});
